import { useState, useMemo } from 'react'
import {
  ApplicationBodyContainer, 
  ApplicationBodyContent,
  DefaultTitle,
  FormContainer,
} from 'src/components/application/defaultComponents'
import {
  FlexDiv,
  FormItemTitle
} from 'src/components/application/formComponents/styles'
import FormItemComponent from 'src/components/application/formComponents/QuestionItemGeneralLayout'
import OptionButtons from 'src/components/application/formComponents/OptionButtons'
import { LeftBar } from 'src/components/basic/LeftBar'
import ReminderCard from 'src/components/basic/ReminderCard'
import PrebuiltRenewalFooter from 'src/components/application/PrebuiltRenewalFooter'
import { ValidationError } from 'yup'
import { RenewalData } from 'src/dtos/renewalData'
import { useRenewal } from 'src/context/renewal'
import StandardInput from 'src/components/basic/StandardInput'
import { isJudgeClaimsTriggered } from 'src/utils/renewals'
import { JudgeQuestionsMainSchema } from 'src/yup/RenewalSchema'
import { ValidationErrorMessage } from 'src/components/basic/ValidationErrorMessage/ValidationErrorMessage'

const Main = () => {
  const { renewalData } = useRenewal()
  const [questions, setQuestions] = useState(renewalData?.judgeRenewalSection?.questions ?? {})
  const [isIncorrect, setIsIncorrect] = useState(false)
  
 

  const updatedApplicationData = (): RenewalData => ({
    ...renewalData,
    currentPage: window.location.pathname,
    judgeRenewalSection: {
      ...renewalData?.judgeRenewalSection,
      questions: {
        ...renewalData?.judgeRenewalSection?.questions,
        ...questions
      }
    }
  })

  const validatePage = useMemo(() => {
    try {
      JudgeQuestionsMainSchema.validateSync(questions, { abortEarly: false })
      return []
    } catch(e) {
      const { errors } = e as ValidationError

      return errors
    }
  }, [questions, JudgeQuestionsMainSchema])
  
  const beforeSaveContinueClick = () => {
    if(validatePage.length > 0) {
      setIsIncorrect(true)
      document.getElementById(validatePage[0])?.scrollIntoView({ behavior: 'smooth' })
      return
    }
  }

  const nextRoute = () => {
    if(isJudgeClaimsTriggered(updatedApplicationData())) {
      return '/customer/renew/judge/questions/claims'
    }
    return '/customer/renew/judge/quote/details'
  }
  
  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent>
        <DefaultTitle>Please answer these questions regarding the current year.</DefaultTitle>
        <FormContainer maxWidth='100%' style={{marginTop: '96px'}}>
          <FlexDiv gap="160px" dir="column">
            <FormItemComponent
              step={1}
              title='In the last 12 months, has the applicant been the subject of a professional or judicial liability claim?'
              maxWidth='100%'
              isValid={!validatePage.includes('step1')}
            >
              <OptionButtons
                option={questions?.hasBeenSubjectOfClaim}
                setOption={(option) => setQuestions(prev => ({...prev, hasBeenSubjectOfClaim: option}))}
                isIncorrect={isIncorrect && questions?.hasBeenSubjectOfClaim === undefined}
                errorCode='EC142'
                
              />
              <ValidationErrorMessage errorCode='EC142' />
              {questions?.hasBeenSubjectOfClaim === true ? (
                <>
                  <FlexDiv gap="24px">
                    <LeftBar />
                    <FlexDiv dir="column">
                      <FormItemTitle>Has the claim been previously reported to TLIE?</FormItemTitle>
                      <OptionButtons
                        option={questions?.hasClaimBeenReported}
                        setOption={(option) => setQuestions(prev => ({...prev, hasClaimBeenReported: option}))}
                        isIncorrect={isIncorrect && questions?.hasClaimBeenReported === undefined}
                        errorCode='EC143'
                      />
                    </FlexDiv>
                    <ValidationErrorMessage errorCode='EC143' />
                  </FlexDiv>
                  {questions?.hasClaimBeenReported === false ? (
                    <FlexDiv maxWidth='460px'>
                      <ReminderCard
                        icon='bulb'
                        title="Additional Information Required"
                      >
                        You will need to fill out additional claim information on the next page regarding this answer.
                      </ReminderCard>
                    </FlexDiv>
                  ) : null}
                </>
              ) : null}
            </FormItemComponent>
            <FormItemComponent
              step={2}
              title='Does the applicant know, have reason to know, or have any basis to believe that a circumstance, act, error or omission might reasonably be expected to be the basis for a professional or judicial liability claim against the applicant?'
              maxWidth='100%'
              isValid={!validatePage.includes('step2')}
            >
              <OptionButtons
                option={questions?.hasBasisForClaim}
                setOption={(option) => setQuestions(prev => ({...prev, hasBasisForClaim: option}))}
                isIncorrect={isIncorrect}
                errorCode='EC144'
              />
              <ValidationErrorMessage errorCode='EC144' />
              {questions?.hasBasisForClaim === true ? (
                <>
                  <FlexDiv gap="24px">
                    <LeftBar />
                    <FlexDiv dir="column">
                      <FormItemTitle>Has the claim been previously reported to TLIE?</FormItemTitle>
                      <OptionButtons
                        option={questions?.hasBasisForClaimReported}
                        setOption={(option) => setQuestions(prev => ({...prev, hasBasisForClaimReported: option}))}
                        isIncorrect={isIncorrect && questions?.hasBasisForClaimReported === undefined}
                        errorCode='EC145'
                      />
                    </FlexDiv>
                    <ValidationErrorMessage errorCode='EC145' />
                  </FlexDiv>
                  {questions?.hasBasisForClaimReported === false ? (
                    <FlexDiv maxWidth='460px'>
                      <ReminderCard
                        icon='bulb'
                        title="Additional Information Required"
                      >
                        You will need to fill out additional claim information on the next page regarding this answer.
                      </ReminderCard>
                    </FlexDiv>
                  ) : null}
                </>
              ) : null}
            </FormItemComponent>
            <FormItemComponent
              step={3}
              title='Has the applicant been the subject of a judicial disciplinary inquiry or action?'
              maxWidth='100%'
              isValid={!validatePage.includes('step3')}
            >
              <OptionButtons
                option={questions?.hasBeenSubjectOfAction}
                setOption={(option) => setQuestions(prev => ({...prev, hasBeenSubjectOfAction: option}))}
                isIncorrect={isIncorrect}
                errorCode='EC146'
              />
              <ValidationErrorMessage errorCode='EC146' />
              {questions?.hasBeenSubjectOfAction === true ? (
                <FlexDiv gap="24px">
                  <LeftBar />
                  <StandardInput
                    label="Please provide full details"
                    rows={6}
                    value={questions?.hasBeenSubjectOfActionDetails ?? ''}
                    onChange={({ target: { value }}) => setQuestions(prev => ({ ...prev, hasBeenSubjectOfActionDetails: value }))}
                    isIncorrect={isIncorrect && !questions?.hasBeenSubjectOfActionDetails}
                    errorMessage={(isIncorrect && !questions?.hasBeenSubjectOfActionDetails) ? 'This is a required field' : ''}
                    errorCode='EC147'
                  />
                </FlexDiv>
              ) : null}
            </FormItemComponent>
          </FlexDiv>
        </FormContainer>
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute="/customer/renew/judge/basic/main"
        isFormValid={validatePage.length === 0}
        nextRoute={nextRoute()}
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={beforeSaveContinueClick}
      />
    </ApplicationBodyContainer>
  )
}

export default Main